import { Formik, Field, Form, ErrorMessage } from "formik";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import * as Yup from "yup";
import {
  useGetCaptchaImageQuery,
  useGetFeedbackDataMutation,
} from "../../redux/services/apiHome";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";

import { useLocation } from "react-router-dom";
import useSeo from "../../hooks/useSeo";
import { useState } from "react";

const Feedback = () => {
  const [captchaMessage, setCaptchaMessage] = useState("");

  const {
    data: captchaImageData = {},
    isLoading: captchaImageIsLoading,
    refetch,
  } = useGetCaptchaImageQuery();

  const captchaImageUrl = captchaImageData?.data?.url;
  const sid = captchaImageData?.data?.sid;
  const token = captchaImageData?.data?.token;

  // for SEO
  const { pathname } = useLocation();
  const { innerBannerTitle, innerBannerImage } = useSeo({ pathname });

  const initialValues = {
    name: "",
    address: "",
    email: "",
    phoneNumber: "",
    message: "",
    captchaCode: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Name can only contain letters and spaces")
      .required("Please enter name"),
    address: Yup.string()
      .matches(
        /^[^<>#$@]*$/,
        "Special characters like < > # $ @ are not allowed"
      )
      .required("Enter your Address"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Enter your Email Address"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone number must be only 10 digits")
      .required("Enter Phone Number"),
    message: Yup.string()
      .max(500, "Message cannot exceed 500 characters")
      .matches(
        /^[^<>#$@]*$/,
        "Special characters like < > # $ @ are not allowed"
      )
      .required("Message is required"),
    captchaCode: Yup.string().required("Enter CAPTCHA Code"),
  });

  const [postFeedbackFnc, { data: feedback, isLoading, isError, isSuccess }] =
    useGetFeedbackDataMutation();

  const { getText } = useLanguageTranslation();

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      name: values.name,
      address: values.address,
      email: values.email,
      phone_no: values.phoneNumber,
      message: values.message,
      captchaCode: values.captchaCode,
      sid,
      token,
    };
    // console.log(payload);

    try {
      await postFeedbackFnc({
        url: "/vi/feedback",
        payload: JSON.stringify(payload),
      }).unwrap();
      alert("Feedback submitted successfully!");
      setCaptchaMessage("");
      resetForm();
      refetch();
    } catch (error) {
      // CAPTCHA-specific error handling
      if (error.status === 400 || error.data?.status === "error") {
        setCaptchaMessage(
          error.data?.message || "Invalid CAPTCHA. Please try again."
        );
      } else {
        // General error handling
        setCaptchaMessage("");
        console.error("Failed to submit feedback:", error);
      }
      refetch(); //
    }
  };

  return (
    <>
      <InnerBannerBreadcrumb
        bannerImage={innerBannerImage}
        title={innerBannerTitle}
        isActive={innerBannerTitle}
        titleLink="/feedback"
        levelTwo={false}
        levelThree={false}
      />

      <div className="inner-container">
        <div className="container">
          <section className="ourCompany">
            <div className="container">
              <div className="row mb-5">
                <div className="col-12 col-sm-12 col-md-12 col-lg-8 mvDescWrap mx-auto "></div>
                <div className="col-12 col-sm-12 col-md-6 col-lg-8 mx-auto">
                  <div className="feedback-inner">
                    <Formik
                      initialValues={initialValues}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ resetForm, touched, errors }) => (
                        <Form>
                          <div className="row">
                            <div className="col-12 col-sm-12 col-md-6 col-lg-6 print-contact1 mb-3">
                              <div className="">
                                <label htmlFor="name" id="name-label">
                                  {getText("Name", "नाम")}
                                  <span className="mandatory-field">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="name"
                                  id="name"
                                  className="form-control"
                                  aria-labelledby="name-label name-error"
                                  aria-required="true"
                                  aria-invalid={
                                    touched.name && errors.name
                                      ? "true"
                                      : "false"
                                  }
                                />
                                <ErrorMessage name="name">
                                  {(errorMsg) => (
                                    <div
                                      id="name-error"
                                      className="error-message"
                                      role="alert"
                                      aria-live="assertive"
                                    >
                                      {errorMsg}
                                    </div>
                                  )}
                                </ErrorMessage>
                              </div>
                            </div>
                            <div className="col-12 col-sm-12 col-md-6 col-lg-6 print-contact1 mb-3">
                              <div className="">
                                <label htmlFor="address" id="address-label">
                                  {getText("Address", "पता ")}
                                  <span className="mandatory-field">*</span>
                                </label>
                                <Field
                                  as="textarea"
                                  name="address"
                                  className="form-control"
                                  rows="1"
                                  aria-labelledby="address-label address-error"
                                  aria-required="true"
                                  aria-invalid={
                                    touched.address && errors.address
                                      ? "true"
                                      : "false"
                                  }
                                />

                                <ErrorMessage name="address">
                                  {(errorMsg) => (
                                    <div
                                      id="address-error"
                                      className="error-message"
                                      role="alert"
                                      aria-live="assertive"
                                    >
                                      {errorMsg}
                                    </div>
                                  )}
                                </ErrorMessage>
                              </div>
                            </div>
                            <div className="col-12 col-sm-12 col-md-6 col-lg-6 print-contact1 mb-3">
                              <div className="">
                                <label htmlFor="email" id="email-label">
                                  {getText("Email", "ईमेल")}
                                  <span className="mandatory-field">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="email"
                                  id="email"
                                  className="form-control"
                                  aria-labelledby="email-label email-error"
                                  aria-required="true"
                                  aria-invalid={
                                    touched.email && errors.email
                                      ? "true"
                                      : "false"
                                  }
                                />

                                <ErrorMessage name="email">
                                  {(errorMsg) => (
                                    <div
                                      id="email-error"
                                      className="error-message"
                                      role="alert"
                                      aria-live="assertive"
                                    >
                                      {errorMsg}
                                    </div>
                                  )}
                                </ErrorMessage>
                              </div>
                            </div>
                            <div className="col-12 col-sm-12 col-md-6 col-lg-6 print-contact1 mb-3">
                              <div className="">
                                <label
                                  htmlFor="phoneNumber"
                                  id="phoneNumber-label"
                                >
                                  {getText("Phone Number", "फोन नंबर")}
                                  <span
                                    className="mandatory-field"
                                    id="phoneNumber-label"
                                  >
                                    *
                                  </span>
                                </label>
                                <Field
                                  type="text"
                                  name="phoneNumber"
                                  className="form-control"
                                  maxLength="10" // Restrict input length to 10
                                  pattern="\d*" // Allow only digits
                                  onInput={(e) => {
                                    e.target.value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    ); // Remove non-digit characters
                                  }}
                                  aria-labelledby="phoneNumber-label phoneNumber-error"
                                  aria-required="true"
                                  aria-invalid={
                                    touched.phoneNumber && errors.phoneNumber
                                      ? "true"
                                      : "false"
                                  }
                                />

                                <ErrorMessage name="phoneNumber">
                                  {(errorMsg) => (
                                    <div
                                      id="phoneNumber-error"
                                      className="error-message"
                                      role="alert"
                                      aria-live="assertive"
                                    >
                                      {errorMsg}
                                    </div>
                                  )}
                                </ErrorMessage>
                              </div>
                            </div>
                            <div className="col-12 col-sm-12 col-md-12 col-lg-12  mb-3">
                              <div className="">
                                <label htmlFor="message" id="message-label">
                                  {getText("Message", "संदेश")}
                                  <span className="mandatory-field">*</span>
                                </label>
                                <Field
                                  as="textarea"
                                  name="message"
                                  className="form-control"
                                  id="message"
                                  aria-labelledby="message-label message-error"
                                  aria-required="true"
                                  aria-invalid={
                                    touched.message && errors.message
                                      ? "true"
                                      : "false"
                                  }
                                />

                                <ErrorMessage name="message">
                                  {(errorMsg) => (
                                    <div
                                      id="message-error"
                                      className="error-message"
                                      role="alert"
                                      aria-live="assertive"
                                    >
                                      {errorMsg}
                                    </div>
                                  )}
                                </ErrorMessage>
                              </div>
                            </div>
                            {/* captcha row */}
                            <div className="col-12 col-sm-12 col-md-12 col-lg-12 mt-3">
                              <div className="row align-items-end captcha-row">
                                <div className="col-12  col-md-3 mb-3 captcha-img-wrap">
                                  <div className="captcha-img-section">
                                    <img
                                      src={`${process.env.REACT_APP_IMAGE_URL}/${captchaImageUrl}`}
                                      alt="Captcha"
                                      className="captcha-img"
                                    />
                                  </div>
                                  <div className="captcha-btns">
                                    <button
                                      type="button"
                                      // onClick={handleResetCaptcha}
                                      onClick={() => refetch()}
                                      aria-label="Refresh CAPTCHA"
                                    >
                                      <i className="fa fa-sync reload"></i>
                                    </button>
                                  </div>
                                </div>

                                {/* captcha field */}
                                <div className="col-12 col-md-3 mb-3">
                                  <div className="">
                                    <label
                                      htmlFor="captchaCode"
                                      id="captchaCode-label"
                                    >
                                      {getText("Captcha", "कैप्चा")}
                                      <span className="mandatory-field">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      id="captchaCode"
                                      name="captchaCode"
                                      className="form-control"
                                      maxLength={6}
                                      aria-labelledby="captchaCode-label captchaCode-error"
                                      aria-required="true"
                                      aria-invalid={
                                        touched.captchaCode &&
                                        errors.captchaCode
                                          ? "true"
                                          : "false"
                                      }
                                    />
                                    <ErrorMessage name="captchaCode">
                                      {(errorMsg) => (
                                        <div
                                          id="captchaCode-error"
                                          className="error-message"
                                          role="alert"
                                          aria-live="assertive"
                                        >
                                          {captchaMessage || errorMsg}
                                        </div>
                                      )}
                                    </ErrorMessage>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="center-buttons mt-3">
                            <button type="submit" className="quick-btn">
                              {isLoading
                                ? getText("Submitting...", "सबमिट...")
                                : getText("Submit", "सबमिट")}
                            </button>
                            <button
                              type="button"
                              className="quick-btn gray-btn"
                              onClick={() => {
                                resetForm();
                                setCaptchaMessage();
                              }}
                            >
                              {getText("Reset", "रीसेट")}
                            </button>
                            {isError && (
                              <p className="error-message">{captchaMessage}</p>
                            )}
                            {isSuccess && (
                              <p className="success-message">
                                Feedback submitted successfully!
                              </p>
                            )}
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Feedback;
